/**
 * Auth + identity hooks.
 *
 * `useMe` is the central read of /auth/me — it gives us the user, the active
 * org, the memberships list, and the effective permission slug set for the
 * active org. Permission gates (`useHasPermission`) and the org switcher
 * read from this query, so invalidating it propagates everywhere.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startAuthentication } from "@simplewebauthn/browser";
import { create } from "zustand";

import {
  ApiPaths,
  api,
  cancelProactiveRefresh,
  registerAuthFailureHandler,
  scheduleProactiveRefresh,
} from "./api";
import { tokenStore } from "./tokenStore";
import type { MePayload, TokenPair } from "./types";

const ME_KEY = ["me"] as const;

type AuthFailureStore = {
  redirectToLogin: boolean;
  setRedirect: (v: boolean) => void;
};

export const useAuthFailureStore = create<AuthFailureStore>((set) => ({
  redirectToLogin: false,
  setRedirect: (v) => set({ redirectToLogin: v }),
}));

registerAuthFailureHandler(() => {
  tokenStore.clear();
  cancelProactiveRefresh();
  useAuthFailureStore.getState().setRedirect(true);
});

// Bootstrap: on a fresh load with a persisted token, arm the background
// refresh so the access token auto-renews before it expires (rather than
// only reactively after a request 401s).
if (tokenStore.getAccess()) {
  scheduleProactiveRefresh();
}

/* --------------- queries --------------- */

export function useMe() {
  return useQuery<MePayload>({
    queryKey: ME_KEY,
    queryFn: async () => {
      const res = await api.get<MePayload>(ApiPaths.authMe);
      return res.data;
    },
    enabled: !!tokenStore.getAccess(),
  });
}

/* --------------- mutations --------------- */

type LoginInput = { email: string; password: string; org_id?: number };

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginInput): Promise<TokenPair> => {
      const res = await api.post<TokenPair>(ApiPaths.authLogin, input);
      return res.data;
    },
    onSuccess: (pair) => {
      tokenStore.setPair(pair.access, pair.refresh);
      scheduleProactiveRefresh(pair.access);
      useAuthFailureStore.getState().setRedirect(false);
      void qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/** Passwordless, discoverable passkey login. Mirrors useLogin's token wiring:
 *  begin → the OS passkey picker (startAuthentication) → complete → tokens. */
export function useWebAuthnLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<TokenPair> => {
      const begin = await api.post<Record<string, unknown> & { state: string }>(
        ApiPaths.webauthnLoginBegin,
        {},
      );
      const { state, ...optionsJSON } = begin.data;
      const credential = await startAuthentication({
        optionsJSON: optionsJSON as never,
      });
      const res = await api.post<TokenPair>(ApiPaths.webauthnLoginComplete, {
        credential,
        state,
      });
      return res.data;
    },
    onSuccess: (pair) => {
      tokenStore.setPair(pair.access, pair.refresh);
      scheduleProactiveRefresh(pair.access);
      useAuthFailureStore.getState().setRedirect(false);
      void qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const refresh = tokenStore.getRefresh();
      if (refresh) {
        try {
          await api.post(ApiPaths.authLogout, { refresh });
        } catch {
          /* tolerate already-blacklisted */
        }
      }
    },
    onSettled: () => {
      tokenStore.clear();
      cancelProactiveRefresh();
      qc.clear();
    },
  });
}

export function isAuthenticated(): boolean {
  return !!tokenStore.getAccess();
}

export const auth = {
  logout: () => {
    tokenStore.clear();
  },
};